import { useSyncExternalStore } from "react";
import { httpsCallable } from "@firebase/functions";
import { v4 as uuidv4 } from "uuid";
import { auth, functions } from "./firebase";
import { uploadChatAttachment } from "./data";
import { diag } from "./diag";
import { retryVideoUpload, startVideoUpload } from "./video-upload";
import { getLocalVideoInfo, restoreLocalVideo, type LocalVideoInfo } from "./native-media-picker";
import type { Message } from "../types";

/**
 * Chat messages on their way to the server: text, photos, files, voice
 * messages and videos.
 *
 * A message shows in the chat the moment it's sent and is saved on the device
 * (IndexedDB, with its photo/file/recording) until the server has it. So a
 * message is never lost: not when sending fails, not when the connection is
 * gone, not when the app is closed meanwhile. Whatever isn't sent yet is sent
 * again when the app starts, comes back to the foreground or gets its
 * connection back. If it still doesn't go through, it stays in the chat
 * marked "Nije poslano", with "Pošalji ponovno" and "Obriši".
 *
 * Sending again is always safe: the message id is chosen here
 * (clientMessageId), so the server never saves a message twice, and an
 * upload that worked is reused.
 */

export type OutboxKind = "text" | "image" | "file" | "audio" | "video";

/** What's saved on the device. */
interface StoredEntry {
  clientMessageId: string;
  groupId: string;
  senderId: string;
  createdAt: string;
  kind: OutboxKind;
  content?: string;
  reply?: { id: string; content: string; senderName: string };
  /** The photo, file, recording or video (left out if too big to keep). */
  file?: Blob;
  fileName?: string;
  mimeType?: string;
  /** A video from the iPhone gallery picker stays on disk; this finds it again. */
  localVideo?: LocalVideoInfo;
  audio?: { durationMs: number; waveform: number[] };
  uploadedUrl?: string;
  uploadedPath?: string;
}

export interface ChatOutboxEntry extends StoredEntry {
  state: "sending" | "failed" | "sent";
  /** Shows the photo/recording/video before it's uploaded. */
  localUrl?: string;
  /** The server refused it (not a connection problem): no automatic retries. */
  refused?: boolean;
  videoJobId?: string;
}

const AUTO_ATTEMPTS = 3;
// Kept on screen after sending until the saved copy has surely arrived.
const KEEP_SENT_MS = 60_000;
// Bigger files are only kept in memory (they're sent while the app is open).
const MAX_SAVED_BYTES = 200 * 1024 * 1024;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Saved copy (IndexedDB)
// ---------------------------------------------------------------------------

const DB_NAME = "nn-chat-outbox";
const STORE = "messages";
let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      try {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => {
          request.result.createObjectStore(STORE, { keyPath: "clientMessageId" });
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }
  return dbPromise;
}

async function dbRequest<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  const db = await openDb();
  if (!db) return undefined;
  return new Promise((resolve) => {
    try {
      const request = run(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
}

function toStored(entry: ChatOutboxEntry): StoredEntry {
  const stored: StoredEntry = {
    clientMessageId: entry.clientMessageId,
    groupId: entry.groupId,
    senderId: entry.senderId,
    createdAt: entry.createdAt,
    kind: entry.kind,
    content: entry.content,
    reply: entry.reply,
    fileName: entry.fileName,
    mimeType: entry.mimeType,
    localVideo: entry.localVideo,
    audio: entry.audio,
    uploadedUrl: entry.uploadedUrl,
    uploadedPath: entry.uploadedPath,
  };
  // Once uploaded, the file itself isn't needed any more.
  if (entry.file && !entry.uploadedUrl && entry.file.size <= MAX_SAVED_BYTES) stored.file = entry.file;
  return stored;
}

function persist(entry: ChatOutboxEntry) {
  void dbRequest("readwrite", (store) => store.put(toStored(entry))).then((result) => {
    if (result === undefined) diag("chat outbox not saved", { kind: entry.kind });
  });
}

function unpersist(clientMessageId: string) {
  void dbRequest("readwrite", (store) => store.delete(clientMessageId));
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

let entries: ChatOutboxEntry[] = [];
const listeners = new Set<() => void>();

function publish(next: ChatOutboxEntry[]) {
  entries = next;
  listeners.forEach((listener) => listener());
}

function find(clientMessageId: string) {
  return entries.find((e) => e.clientMessageId === clientMessageId);
}

function patch(clientMessageId: string, changes: Partial<ChatOutboxEntry>, save = false) {
  publish(entries.map((e) => (e.clientMessageId === clientMessageId ? { ...e, ...changes } : e)));
  const updated = find(clientMessageId);
  if (save && updated) persist(updated);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Messages not confirmed by the server yet (all chats). */
export function useChatOutbox(): ChatOutboxEntry[] {
  return useSyncExternalStore(subscribe, () => entries);
}

export function voiceFileName(extension: string) {
  return `Glasovna poruka.${extension}`;
}

/** The chat message for an entry, shown until the saved copy arrives. */
export function outboxEntryAsMessage(entry: ChatOutboxEntry): Message {
  const base: Message = {
    id: entry.clientMessageId,
    clientMessageId: entry.clientMessageId,
    groupId: entry.groupId,
    type: "user",
    senderId: entry.senderId,
    createdAt: entry.createdAt,
    status: entry.state === "sent" ? "sent" : "pending",
    content: entry.content,
    sendFailed: entry.state === "failed",
    replyToId: entry.reply?.id,
    replyToContent: entry.reply?.content,
    replyToSenderName: entry.reply?.senderName,
  };
  switch (entry.kind) {
    case "image":
      return { ...base, mediaType: "image", imageUrl: entry.localUrl || entry.uploadedUrl };
    case "file":
      return { ...base, fileName: entry.fileName, fileUrl: entry.uploadedUrl };
    case "audio":
      return {
        ...base,
        mediaType: "audio",
        fileName: entry.fileName,
        fileUrl: entry.uploadedUrl,
        audioDuration: entry.audio ? Math.round(entry.audio.durationMs / 100) / 10 : undefined,
        audioWaveform: entry.audio?.waveform,
        localAudioUrl: entry.localUrl,
      };
    case "video":
      return entry.localUrl
        ? { ...base, mediaType: "video", videoUrl: entry.localUrl, fileName: entry.fileName }
        : // A video still on the iPhone's disk has no preview: its name shows.
          { ...base, fileName: entry.fileName };
    default:
      return base;
  }
}

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

type Payload = Record<string, unknown>;

function errorCode(error: unknown) {
  return (error as { code?: string })?.code || "";
}

/** The server said no: trying again the same way won't help. */
function isRefusal(error: unknown) {
  return /permission-denied|not-found|invalid-argument|unauthenticated|storage\/unauthorized|nn\/no-file/.test(errorCode(error));
}

// Messages of a chat are saved in the order they were sent: each waits for
// the one before it (sent or failed), but never longer than this, so one slow
// upload can't hold up the chat. Uploads still run at the same time.
const ORDER_WAIT_MS = 20_000;
const lastInChat = new Map<string, Promise<void>>();
const turns = new Map<string, Promise<void>>();

/** Takes this message's place in its chat's line; returns "done" for the next one. */
function takeTurn(entry: ChatOutboxEntry): () => void {
  const previous = lastInChat.get(entry.groupId) ?? Promise.resolve();
  let done!: () => void;
  const mine = new Promise<void>((resolve) => (done = resolve));
  lastInChat.set(entry.groupId, mine);
  turns.set(
    entry.clientMessageId,
    Promise.race([previous, new Promise<void>((resolve) => setTimeout(resolve, ORDER_WAIT_MS))]),
  );
  return () => {
    turns.delete(entry.clientMessageId);
    done();
  };
}

async function createMessage(entry: ChatOutboxEntry, payload: Payload): Promise<string | undefined> {
  await turns.get(entry.clientMessageId);
  const create = httpsCallable(functions, "createMessage");
  const response = await create({
    type: "user",
    groupId: entry.groupId,
    senderId: entry.senderId,
    clientMessageId: entry.clientMessageId,
    content: entry.content || undefined,
    replyToId: entry.reply?.id,
    replyToContent: entry.reply?.content,
    replyToSenderName: entry.reply?.senderName,
    ...payload,
  });
  const data = (response as { data: { success: boolean; message?: { id: string } } }).data;
  if (!data?.success) throw new Error("Slanje poruke nije uspjelo.");
  return data.message?.id;
}

function fileOf(entry: ChatOutboxEntry): File {
  if (!entry.file) throw Object.assign(new Error("File gone"), { code: "nn/no-file" });
  return entry.file instanceof File
    ? entry.file
    : new File([entry.file], entry.fileName || "datoteka", { type: entry.mimeType || entry.file.type });
}

async function upload(entry: ChatOutboxEntry): Promise<string> {
  if (entry.uploadedUrl) return entry.uploadedUrl;
  const file = entry.kind === "audio"
    ? new File([fileOf(entry)], `glasovna-${entry.clientMessageId}.${entry.fileName?.split(".").pop() || "m4a"}`, { type: entry.mimeType })
    : fileOf(entry);
  const { url } = await uploadChatAttachment(file, entry.groupId);
  patch(entry.clientMessageId, { uploadedUrl: url }, true);
  return url;
}

/** One attempt at sending (not videos, see sendVideo). */
async function attempt(clientMessageId: string): Promise<void> {
  const entry = find(clientMessageId);
  if (!entry) return;
  switch (entry.kind) {
    case "text":
      await createMessage(entry, {});
      return;
    case "image":
      await createMessage(entry, { imageUrl: await upload(entry), mediaType: "image" });
      return;
    case "file":
      await createMessage(entry, { fileUrl: await upload(entry), fileName: entry.fileName });
      return;
    case "audio":
      await createMessage(entry, {
        mediaType: "audio",
        // Older app versions show these as a file to download.
        fileUrl: await upload(entry),
        fileName: entry.fileName,
        audioDuration: entry.audio ? Math.round(entry.audio.durationMs / 100) / 10 : undefined,
        audioWaveform: entry.audio?.waveform,
      });
      return;
  }
}

function markSent(clientMessageId: string, startedAt: number) {
  const entry = find(clientMessageId);
  diag("chat message sent", { kind: entry?.kind, ms: Date.now() - startedAt });
  patch(clientMessageId, { state: "sent", refused: false });
  unpersist(clientMessageId);
  setTimeout(() => forget(clientMessageId), KEEP_SENT_MS);
}

function markFailed(clientMessageId: string, error: unknown) {
  diag("chat message not sent", { code: errorCode(error), message: String(error).slice(0, 120) });
  patch(clientMessageId, { state: "failed", refused: isRefusal(error) });
}

const running = new Set<string>();

async function run(clientMessageId: string) {
  const entry = find(clientMessageId);
  if (!entry || entry.state === "sent" || running.has(clientMessageId)) return;
  if (entry.kind === "video") {
    sendVideo(entry);
    return;
  }
  running.add(clientMessageId);
  patch(clientMessageId, { state: "sending" });
  const startedAt = Date.now();
  const finishTurn = takeTurn(entry);
  try {
    for (let i = 1; ; i++) {
      try {
        await attempt(clientMessageId);
        break;
      } catch (error) {
        diag("chat send attempt failed", { attempt: i, code: errorCode(error) });
        if (i >= AUTO_ATTEMPTS || isRefusal(error)) throw error;
        await new Promise((resolve) => setTimeout(resolve, 1500 * i));
      }
    }
    markSent(clientMessageId, startedAt);
  } catch (error) {
    markFailed(clientMessageId, error);
  } finally {
    running.delete(clientMessageId);
    finishTurn();
  }
}

/** Videos go through the background uploader (progress bar, resumable). */
function sendVideo(entry: ChatOutboxEntry) {
  const { clientMessageId } = entry;
  const startedAt = Date.now();
  const videoPayload = (url: string, storagePath: string) => ({
    videoUrl: url,
    mediaType: "video",
    videoProcessing: true,
    videoProcessingPath: storagePath,
  });
  patch(clientMessageId, { state: "sending" });

  // Uploaded before (e.g. before the app was closed): only the message is missing.
  if (entry.uploadedUrl && entry.uploadedPath) {
    running.add(clientMessageId);
    createMessage(entry, videoPayload(entry.uploadedUrl, entry.uploadedPath))
      .then(() => markSent(clientMessageId, startedAt), (error) => markFailed(clientMessageId, error))
      .finally(() => running.delete(clientMessageId));
    return;
  }
  if (entry.videoJobId && retryVideoUpload(entry.videoJobId)) return;

  let file: File;
  try {
    file = entry.localVideo ? restoreLocalVideo(entry.localVideo) : fileOf(entry);
  } catch (error) {
    markFailed(clientMessageId, error);
    return;
  }
  const videoJobId = startVideoUpload({
    label: "Poruka",
    file,
    folder: "chat-videos",
    metadata: { groupId: entry.groupId },
    onAttempt: () => patch(clientMessageId, { state: "sending" }),
    onFailed: (error) => {
      if (errorCode(error) === "storage/canceled") discardChatMessage(clientMessageId);
      else markFailed(clientMessageId, error);
    },
    onUploaded: async ({ url, storagePath }) => {
      patch(clientMessageId, { uploadedUrl: url, uploadedPath: storagePath }, true);
      const current = find(clientMessageId) ?? entry;
      const id = await createMessage(current, videoPayload(url, storagePath));
      markSent(clientMessageId, startedAt);
      return id ? `groups/${entry.groupId}/messages/${id}` : undefined;
    },
  });
  patch(clientMessageId, { videoJobId });
}

/** Sends a chat message: it shows right away and is kept until the server has it. */
export function queueChatMessage(options: {
  groupId: string;
  senderId: string;
  kind: OutboxKind;
  content?: string;
  file?: Blob;
  fileName?: string;
  mimeType?: string;
  audio?: { durationMs: number; waveform: number[] };
  reply?: StoredEntry["reply"];
}): void {
  const file = options.file;
  const localVideo = file instanceof File ? getLocalVideoInfo(file) : null;
  const entry: ChatOutboxEntry = {
    clientMessageId: uuidv4().replace(/-/g, ""),
    groupId: options.groupId,
    senderId: options.senderId,
    createdAt: new Date().toISOString(),
    kind: options.kind,
    content: options.content,
    reply: options.reply,
    file: localVideo ? undefined : file,
    fileName: options.fileName ?? (file instanceof File ? file.name : undefined),
    mimeType: options.mimeType ?? file?.type,
    localVideo: localVideo ?? undefined,
    audio: options.audio,
    state: "sending",
    localUrl:
      file && !localVideo && ["image", "audio", "video"].includes(options.kind)
        ? URL.createObjectURL(file)
        : undefined,
  };
  publish([...entries, entry]);
  persist(entry);
  void run(entry.clientMessageId);
}

/** "Pošalji ponovno". */
export function retryChatMessage(clientMessageId: string) {
  void run(clientMessageId);
}

function forget(clientMessageId: string) {
  const entry = find(clientMessageId);
  if (!entry) return;
  publish(entries.filter((e) => e.clientMessageId !== clientMessageId));
  // A player may still be playing it.
  if (entry.localUrl) setTimeout(() => URL.revokeObjectURL(entry.localUrl!), 10 * 60_000);
}

/** "Obriši" on a message that wasn't sent. */
export function discardChatMessage(clientMessageId: string) {
  forget(clientMessageId);
  unpersist(clientMessageId);
}

/** Sends again everything that failed for a connection problem. */
function retryFailed(reason: string) {
  const uid = auth.currentUser?.uid;
  const due = entries
    .filter((e) => e.state === "failed" && !e.refused && e.senderId === uid)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  if (!due.length) return;
  diag("chat outbox retry", { reason, count: due.length });
  due.forEach((e) => void run(e.clientMessageId));
}

let loadedFor: string | null = null;

/** Messages left from an earlier session (app closed before they were sent). */
async function loadSaved(uid: string) {
  if (loadedFor === uid) return;
  loadedFor = uid;
  const saved = (await dbRequest<StoredEntry[]>("readonly", (store) => store.getAll())) ?? [];
  const now = Date.now();
  const restored: ChatOutboxEntry[] = [];
  saved.forEach((stored) => {
    if (now - Date.parse(stored.createdAt) > MAX_AGE_MS) {
      unpersist(stored.clientMessageId);
      return;
    }
    if (stored.senderId !== uid || find(stored.clientMessageId)) return;
    restored.push({
      ...stored,
      state: "sending",
      localUrl:
        stored.file && ["image", "audio", "video"].includes(stored.kind) ? URL.createObjectURL(stored.file) : undefined,
    });
  });
  if (!restored.length) return;
  diag("chat outbox restored", { count: restored.length });
  restored.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  publish([...entries, ...restored].sort((a, b) => a.createdAt.localeCompare(b.createdAt)));
  // In the order they were written, so they arrive in that order.
  restored.forEach((e) => void run(e.clientMessageId));
}

let started = false;

/** Sends what's left from before, and retries when the app or connection returns. */
export function startChatOutbox() {
  if (started) return;
  started = true;
  window.addEventListener("online", () => retryFailed("online"));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") retryFailed("app visible");
  });
  auth.onAuthStateChanged((user) => {
    if (user) void loadSaved(user.uid);
    else loadedFor = null;
  });
}
